// src/components/settings/ClinicEmailSettings.jsx
import React, { useState, useEffect } from 'react';
import { FiMail, FiCheck, FiAlertCircle, FiLoader } from 'react-icons/fi';
import { useTenant } from '../../context/TenantContext';
import { tenantDataService } from '../../services/tenantDataService';
import toast from 'react-hot-toast';

const ClinicEmailSettings = () => {
  const { currentTenant } = useTenant();
  const [settings, setSettings] = useState({
    clinicEmail: '',
    clinicName: '',
    replyToEmail: '',
    emailSignature: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    clinicEmail: '',
    clinicName: '',
    replyToEmail: '',
    emailSignature: ''
  });

  useEffect(() => {
    loadEmailSettings();
  }, [currentTenant]);

  const loadEmailSettings = async () => {
    if (!currentTenant?.id) return;
    
    try {
      setLoading(true);
      const result = await tenantDataService.getById('settings', 'email');
      
      if (result) {
        setSettings(result);
        setFormData({
          clinicEmail: result.clinicEmail || '',
          clinicName: result.clinicName || currentTenant.name || '',
          replyToEmail: result.replyToEmail || '',
          emailSignature: result.emailSignature || ''
        });
      } else {
        // Set defaults
        setFormData({
          clinicEmail: '',
          clinicName: currentTenant.name || '',
          replyToEmail: '',
          emailSignature: `Best regards,\n${currentTenant.name || 'Your Clinic'}`
        });
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.clinicEmail || !formData.clinicName) {
      toast.error('Please fill in clinic email and name');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.clinicEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (formData.replyToEmail && !emailRegex.test(formData.replyToEmail)) {
      toast.error('Please enter a valid reply-to email address');
      return;
    }

    try {
      setSaving(true);
      toast.loading('Saving email settings...', { id: 'email-setup' });

      const emailSettings = {
        clinicEmail: formData.clinicEmail,
        clinicName: formData.clinicName,
        replyToEmail: formData.replyToEmail || formData.clinicEmail,
        emailSignature: formData.emailSignature,
        updatedAt: new Date().toISOString()
      };

      await tenantDataService.update('settings', 'email', emailSettings);
      
      setSettings(emailSettings);
      toast.success('Email settings saved successfully!', { id: 'email-setup' });
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error(`Failed to save settings: ${error.message}`, { id: 'email-setup' });
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <FiLoader className="animate-spin text-2xl text-blue-500" />
        <span className="ml-2">Loading email settings...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold flex items-center">
            <FiMail className="mr-2" />
            Clinic Email Configuration
          </h2>
          <p className="text-gray-600 mt-2">
            Configure how your clinic appears in invoice emails sent to clients.
          </p>
        </div>

        <div className="p-6">
          {/* Status Card */}
          <div className="mb-6">
            <div className={`p-4 rounded-lg border ${settings.clinicEmail ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center">
                {settings.clinicEmail ? (
                  <FiCheck className="text-green-500 mr-2" />
                ) : (
                  <FiAlertCircle className="text-gray-400 mr-2" />
                )}
                <span className="font-medium">Email Configuration</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {settings.clinicEmail ? `Configured: ${settings.clinicEmail}` : 'Not configured'}
              </p>
            </div>
          </div>

          {/* Configuration Form */}
          <form onSubmit={handleSubmit} className="space-y-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clinic Name
                </label>
                <input
                  type="text"
                  value={formData.clinicName}
                  onChange={(e) => setFormData(prev => ({ ...prev, clinicName: e.target.value }))}
                  placeholder="Your Clinic Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  This appears as the sender name in emails
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clinic Email Address
                </label>
                <input
                  type="email"
                  value={formData.clinicEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, clinicEmail: e.target.value }))}
                  placeholder="billing@yourclinic.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  This will be the "from" address for invoice emails
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reply-To Email (Optional)
              </label>
              <input
                type="email"
                value={formData.replyToEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, replyToEmail: e.target.value }))}
                placeholder="info@yourclinic.com (leave blank to use clinic email)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Where client replies will be sent (defaults to clinic email)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Signature
              </label>
              <textarea
                value={formData.emailSignature}
                onChange={(e) => setFormData(prev => ({ ...prev, emailSignature: e.target.value }))}
                placeholder="Best regards,\nYour Clinic Name"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                This signature will be added to all invoice emails
              </p>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {saving ? <FiLoader className="animate-spin mr-2" /> : <FiMail className="mr-2" />}
              {saving ? 'Saving...' : 'Save Email Settings'}
            </button>
          </form>

          {/* Current Configuration */}
          {settings.clinicEmail && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium mb-2">Current Configuration</h3>
              <div className="space-y-2 text-sm">
                <div><strong>Clinic Name:</strong> {settings.clinicName}</div>
                <div><strong>Email:</strong> {settings.clinicEmail}</div>
                <div><strong>Reply-To:</strong> {settings.replyToEmail}</div>
                {settings.emailSignature && (
                  <div><strong>Signature:</strong> <pre className="text-xs mt-1 whitespace-pre-wrap">{settings.emailSignature}</pre></div>
                )}
              </div>
            </div>
          )}


          {/* Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <FiMail className="text-blue-500 mr-2" />
              <span className="font-medium text-blue-900">How Email Works</span>
            </div>
            <p className="text-blue-800 mt-2 text-sm">
              Invoice emails will be sent using HealeHub's email service, but will appear to come from your clinic's email address. 
              Clients can reply directly to your clinic email.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicEmailSettings;
